import { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RealtimeSyncProvider } from "@/contexts/RealtimeSyncContext";
import { AchievementNotificationProvider } from "@/contexts/AchievementNotificationContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProductsProvider } from "@/contexts/ProductsContext";
import { AppLayout } from "@/components/AppLayout";
import { AchievementNotification } from "@/components/cabinet/AchievementNotification";
import { ProtectedAdminRoute } from "./components/admin/ProtectedAdminRoute";
import { SEO, defaultOrganizationJsonLd } from "@/components/SEO";

// Lazy load pages for code-splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Favoritos = lazy(() => import("./pages/Favoritos"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Admin = lazy(() => import("./pages/Admin"));
const MeuArmario = lazy(() => import("./pages/MeuArmario"));
const CalendarioEmocional = lazy(() => import("./pages/CalendarioEmocional"));
const Assistente = lazy(() => import("./pages/Assistente"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutos de cache padrão
      gcTime: 10 * 60 * 1000, // Mantém dados em cache por 10 minutos
      refetchOnWindowFocus: false, // Não re-busca ao voltar para a aba
      retry: 1, // Retry apenas 1 vez em caso de erro
    },
  },
});

// Simple loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ErrorBoundary>
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <ThemeProvider defaultTheme="system" storageKey="guia-natural-theme">
        <RealtimeSyncProvider>
          <AchievementNotificationProvider>
              <TooltipProvider>
                <ProductsProvider>
                {/* SEO padrão para todas as páginas */}
                <SEO jsonLd={defaultOrganizationJsonLd} />
                <Toaster />
                <BrowserRouter>
                  <AppLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/perfil" element={<Profile />} />
                        <Route path="/meu-armario" element={<MeuArmario />} />
                        <Route path="/calendario-emocional" element={<CalendarioEmocional />} />
                        <Route path="/assistente" element={<Assistente />} />
                        <Route path="/favoritos" element={<Favoritos />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogArticle />} />
                        <Route
                          path="/admin"
                          element={
                            <ProtectedAdminRoute>
                              <Admin />
                            </ProtectedAdminRoute>
                          }
                        />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/privacidade" element={<Privacidade />} />
                        <Route path="/termos" element={<Termos />} />
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </AppLayout>
                  <AchievementNotification />
                </BrowserRouter>
                </ProductsProvider>
            </TooltipProvider>
          </AchievementNotificationProvider>
        </RealtimeSyncProvider>
      </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
  </ErrorBoundary>
);

export default App;
